import React, {useCallback, useState} from 'react';
import AddCircleRounded from '@material-ui/icons/AddCircleRounded';
import OpenInNewIcon from '@material-ui/icons/OpenInNew';
import fetchUser from '../../../utils/gaia/fetchUser';
import isLayerTypeAuthorized from '../../../utils/gaia/isLayerTypeAuthorized';
import List from '@material-ui/core/List';
import ListItem from '@material-ui/core/ListItem';
import RemoveCircleRounded from '@material-ui/icons/RemoveCircleRounded';
import DeleteForeverIcon from '@material-ui/icons/DeleteForever';
import {Sidebar, SidebarHead, SidebarBody, SidebarProps, sidebarWidth} from './Sidebar';
import trackLayerAnalytics from '../../../utils/gaia/trackLayerAnalytics';
import theme from '../../../theme';
import Typography from '@material-ui/core/Typography';
import Button from '@material-ui/core/Button';
import {UserLayerDispatcher} from '../../../hooks/useLayers';
import useSyncPromise from '../../../utils/react/useSyncPromise';

export interface LayerDetailsSidebarProps extends SidebarProps {
    sidebar: LayerDetailsSidebar;
    userLayers: UserLayer[] | undefined;
    userLayerDispatcher: UserLayerDispatcher;
    onSidebarChange: (sidebar: MapSidebar) => void;
}

export default function LayerDetailsSidebar(props: LayerDetailsSidebarProps) {
    const {userLayers, onSidebarChange, userLayerDispatcher} = props;
    const {layerType, prevSidebar} = props.sidebar;
    const [user] = useSyncPromise(fetchUser());
    const [isLegendScaled, setIsLegendScaled] = useState(false);

    const isAuthorized = isLayerTypeAuthorized(layerType, user);

    const isSelected = userLayers && userLayers.find(((userLayer: UserLayer) =>
        layerType.uniqueTilecacheKey === userLayer.layer.type
    ));

    const isCustomSource = layerType.categories[0] === 'custom';

    const onRemoveClick = useCallback(() => {
        userLayerDispatcher({type: 'remove', layer: layerType});
    }, [layerType, userLayerDispatcher]);

    const onAddClick = useCallback(() => {
        userLayerDispatcher({type: 'add', layer: layerType});
    }, [layerType, userLayerDispatcher]);

    const legendProps = {
        href: layerType.legend_url,
        target: '_blank',
        rel: 'noopener noreferrer',
    };

    return <Sidebar {...props}>
        <SidebarHead
            prevSidebar={prevSidebar}
            onSidebarChange={onSidebarChange}
        >
            {isSelected && isCustomSource && <Button
                color='secondary'
                startIcon={<DeleteForeverIcon />}
                onClick={onRemoveClick}
                style={{fontSize: 14}}
            >
                Delete
            </Button>}

            {isSelected && !isCustomSource && <Button
                color='secondary'
                startIcon={<RemoveCircleRounded />}
                onClick={onRemoveClick}
                style={{fontSize: 14}}
            >
                Remove Layer
            </Button>}

            {!isSelected && isAuthorized && !isCustomSource && <Button
                color="primary"
                startIcon={<AddCircleRounded />}
                onClick={onAddClick}
                style={{fontSize: 14}}
            >Add Layer</Button>}
        </SidebarHead>

        <SidebarBody>
            <Typography
                variant='h5'
                style={{
                    wordBreak: 'break-word',
                    padding: theme.spacing(2),
                    fontWeight: 'bold',
                }}
            >
                {layerType.title}
            </Typography>

            {layerType.icon_url && <img
                src={layerType.icon_url.replace(/\/80\/80/g, '/308/308')}
                style={{width: sidebarWidth, height: sidebarWidth}}
            />}

            <List disablePadding>
                {layerType.disclaimer && isAuthorized &&
                <ListItem divider style={{flexDirection: 'column', alignItems: 'start'}}>
                    <Typography variant='caption' color='secondary'>Warning</Typography>
                    <Typography variant='body1' style={{paddingTop: theme.spacing(0.5)}}>
                        {layerType.disclaimer}
                    </Typography>
                </ListItem>}

                <ListItem divider style={{flexDirection: 'column', alignItems: 'start'}}>
                    <Typography variant='caption'>Description</Typography>
                    <Typography variant='body1' style={{paddingTop: theme.spacing(0.5)}}>
                        {layerType.description}
                    </Typography>
                </ListItem>

                <ListItem divider style={{flexDirection: 'column', alignItems: 'start'}}>
                    <Typography variant='caption'>Attribution</Typography>
                    <Typography variant='body1' style={{paddingTop: theme.spacing(0.5)}}>
                        {layerType.attribution}
                    </Typography>
                </ListItem>

                <ListItem divider style={{flexDirection: 'column', alignItems: 'start'}}>
                    <Typography variant='caption'>Zoom Range</Typography>
                    <Typography variant='body1' style={{paddingTop: theme.spacing(0.5)}}>
                        {layerType.minZoom} - {layerType.maxZoom}
                    </Typography>
                </ListItem>

                {layerType.legend_url && isAuthorized &&
                    <ListItem style={{flexDirection: 'column', alignItems: 'start'}}>
                        <Typography variant='caption'>Legend</Typography>

                        {isLegendScaled && <Button
                            variant='outlined'
                            component='a'
                            {...legendProps}
                            color='primary'
                            startIcon={<OpenInNewIcon />}
                            style={{marginTop: theme.spacing(0.5)}}
                        >
                            Open in New Window
                        </Button>}

                        <a
                            {...legendProps}
                            onClick={() => trackLayerAnalytics('View Legend', userLayers, {
                                layerName: layerType.uniqueTilecacheKey,
                                category: 'map2',
                            })}
                            style={{marginTop: theme.spacing(0.5)}}
                        >
                            <img
                                style={{maxWidth: '100%'}}
                                src={layerType.legend_url}
                                onLoad={event => {
                                    const img = event.target as HTMLImageElement;
                                    setTimeout(() => setIsLegendScaled(
                                        img.naturalWidth > img.width ||
                                        img.naturalHeight > img.height
                                    ));
                                }}
                            />
                        </a>
                    </ListItem>}
            </List>
        </SidebarBody>
    </Sidebar>;
}
